import React from 'react';
import { Box, Paper, Typography, Button, CircularProgress } from '@mui/material';
import { Block } from '@mui/icons-material';
import { useSubscription } from '../context/SubscriptionContext';
import { useModulePermissions } from '../hooks/useModulePermissions';
import { useAuth } from '../context/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /**
   * Required subscription feature (e.g., 'examination', 'attendance')
   * If not provided, route doesn't require subscription feature
   */
  requiredFeature?: string;
  /**
   * Required permission module (e.g., 'dashboard', 'users')
   * If not provided, route doesn't require specific permission
   */
  requiredPermission?: string;
  /**
   * Permission action ('read' or 'write')
   * Defaults to 'read' if requiredPermission is provided
   */
  permissionAction?: 'read' | 'write';
}

/**
 * ProtectedRoute Component
 * 
 * Protects routes by checking:
 * 1. Subscription features (if requiredFeature is provided)
 * 2. User permissions (if requiredPermission is provided)
 * 
 * Shows an elegant access denied page if user doesn't have access.
 * System Admin bypasses all checks.
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredFeature,
  requiredPermission,
  permissionAction = 'read',
}) => {
  const { user, decodedToken, loading } = useAuth();
  const { hasFeature, isLoading: subscriptionLoading } = useSubscription();
  const { canRead, canWrite } = useModulePermissions();

  // Show loading state while auth or subscription is loading
  if (loading || subscriptionLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="70vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // System Admin bypasses all checks
  if (user?.isSystemAdmin || decodedToken?.isSystemAdmin) {
    return <>{children}</>;
  }

  // Check subscription feature access
  if (requiredFeature) {
    // 'all' feature is only for system admin
    if (requiredFeature === 'all' && !user?.isSystemAdmin && !decodedToken?.isSystemAdmin) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="70vh"
          sx={{ p: 3 }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 5,
              textAlign: 'center',
              maxWidth: 420,
              border: '1px solid #e0e0e0',
              borderRadius: 2,
            }}
          >
            <Block
              sx={{
                fontSize: 56,
                color: '#9e9e9e',
                mb: 2
              }}
            />
            <Typography variant="h6" fontWeight="500" gutterBottom color="text.primary">
              Access Denied
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              This page is only accessible to system administrators.
            </Typography>
            <Button
              variant="contained"
              onClick={() => window.history.back()}
              sx={{ mt: 3, textTransform: 'none' }}
            >
              Go Back
            </Button>
          </Paper>
        </Box>
      );
    }

    // Check other features
    if (requiredFeature !== 'all' && !hasFeature(requiredFeature)) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="70vh"
          sx={{ p: 3 }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 5,
              textAlign: 'center',
              maxWidth: 420,
              border: '1px solid #e0e0e0',
              borderRadius: 2,
            }}
          >
            <Block
              sx={{
                fontSize: 56,
                color: '#9e9e9e',
                mb: 2
              }}
            />
            <Typography variant="h6" fontWeight="500" gutterBottom color="text.primary">
              Subscription Required
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              This module is not included in your current subscription plan.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Please contact your administrator to upgrade your plan.
            </Typography>
            <Button
              variant="contained"
              onClick={() => window.history.back()}
              sx={{ mt: 3, textTransform: 'none' }}
            >
              Go Back
            </Button>
          </Paper>
        </Box>
      );
    }
  }

  // Check permission access
  if (requiredPermission) {
    const hasPermission = permissionAction === 'write'
      ? canWrite(requiredPermission)
      : canRead(requiredPermission);

    if (!hasPermission) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="70vh"
          sx={{ p: 3 }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 5,
              textAlign: 'center',
              maxWidth: 420,
              border: '1px solid #e0e0e0',
              borderRadius: 2,
            }}
          >
            <Block
              sx={{
                fontSize: 56,
                color: '#9e9e9e',
                mb: 2
              }}
            />
            <Typography variant="h6" fontWeight="500" gutterBottom color="text.primary">
              Access Denied
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              You don't have permission to view this page.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Contact your administrator for access.
            </Typography>
            <Button
              variant="contained"
              onClick={() => window.history.back()}
              sx={{ mt: 3, textTransform: 'none' }}
            >
              Go Back
            </Button>
          </Paper>
        </Box>
      );
    }
  }

  // All checks passed, render the route
  return <>{children}</>;
};

export default ProtectedRoute;
